import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import { useState } from "react"
import type { Key } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Text from "@/globalComponents/atoms/Text"
import FilterTabs from "@/globalComponents/molecules/FilterTabs"
import { ReservationConfirmationCard } from "@/globalComponents/molecules/productCards/ReservationCard"
import { CancelConfirmedModal } from "@/globalComponents/molecules/productCards/ReservationCard/reservationModals/CancelConfirmedModal"
import {
  cancelReservationMutationOptions,
  currentReservationsQueryOptions,
  pastReservationsQueryOptions,
  updateReservationPlateMutationOptions,
} from "@/utilities/hooks/queries/reserve"
import { themeQueryOptions } from "@/utilities/hooks/queries/shared"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useTheme } from "@/utilities/hooks/useTheme"
import { Crumb, ScannedAreaSearch } from "@/utilities/types/local"

import styles from "./reservations.module.css"

export const Route = createFileRoute("/_authenticated/account/reservations")({
  staticData: {
    pageName: "Reservations",
  },
  validateSearch: (search: Record<string, unknown>): ScannedAreaSearch => {
    const qrLookupID = (search.qrLookupID as string) || ""
    if (qrLookupID) {
      return { qrLookupID }
    }
    return {}
  },
  loaderDeps: ({ search }) => ({
    qrLookupID: search.qrLookupID,
  }),
  loader: async ({ context, deps }) => {
    const theme = await context.queryClient.ensureQueryData(
      themeQueryOptions({ qrLookupID: deps.qrLookupID || null })
    )
    const locationID = theme?.locationInformation.locationID || null
    await Promise.all([
      context.queryClient.ensureQueryData(
        currentReservationsQueryOptions(locationID)
      ),
      context.queryClient.ensureQueryData(
        pastReservationsQueryOptions(locationID)
      ),
    ])
    return { crumb: { translationKey: "common:reservations.header" } as Crumb }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation("common")
  const [selectedTab, setSelectedTab] = useState<Key>("active")
  const [cancelConfirmedOpen, setCancelConfirmedOpen] = useState(false)
  const queryClient = useQueryClient()
  const { scannedArea } = useTheme()
  const locationID = scannedArea?.theme.locationInformation.locationID || null
  const { qrLookupID } = Route.useSearch()

  const { data: currentReservations } = useSuspenseQuery(
    currentReservationsQueryOptions(locationID)
  )
  const { data: pastReservations } = useSuspenseQuery(
    pastReservationsQueryOptions(locationID)
  )

  const { setError } = useErrorModal()
  const cancelReservationMutation = useMutation({
    ...cancelReservationMutationOptions(queryClient),
    onSuccess: () => setCancelConfirmedOpen(true),
    onError: (error) => setError({ error }),
  })
  const { mutate: handleUpdatePlate } = useMutation({
    ...updateReservationPlateMutationOptions(queryClient, locationID),
    onError: (error) => setError({ error }),
  })

  const displayedReservations =
    selectedTab === "active" ? currentReservations : pastReservations

  return (
    <>
      <FilterTabs onSelectionChange={setSelectedTab} selectedKey={selectedTab}>
        <FilterTabs.Tab id="active">{t("reservations.active")}</FilterTabs.Tab>
        <FilterTabs.Tab id="inactive">
          {t("reservations.inactive")}
        </FilterTabs.Tab>
      </FilterTabs>

      <AnimatePresence mode="wait">
        {displayedReservations.length === 0 ? (
          <motion.div
            key={`empty-${selectedTab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Text level={2} className={styles.emptyState}>
              {selectedTab === "active"
                ? t("reservations.emptyActive")
                : t("reservations.emptyInactive")}
            </Text>
          </motion.div>
        ) : (
          <motion.div
            key={`list-${selectedTab}`}
            className={styles.cardList}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
          >
            {displayedReservations.map((reservation) => (
              <motion.div
                key={reservation.reservationID}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { ease: "easeIn" },
                  },
                }}
              >
                <ReservationConfirmationCard
                  reservation={reservation}
                  qrLookupID={qrLookupID}
                  contextLocationID={locationID}
                  {...(selectedTab === "active" && {
                    onSelectPlate: (plate: string) =>
                      handleUpdatePlate({
                        newPlate: plate,
                        reservationID: reservation.reservationID,
                        receiptID:
                          reservation.receiptIDs[
                            reservation.receiptIDs.length - 1
                          ],
                      }),
                    isCancelling:
                      cancelReservationMutation.isPending &&
                      cancelReservationMutation.variables?.reservationID ===
                        reservation.reservationID,
                    onCancelReservation: () =>
                      cancelReservationMutation.mutateAsync({
                        reservationID: reservation.reservationID,
                        enterpriseID: reservation.enterpriseID,
                        locationID: reservation.locationID,
                      }),
                  })}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <CancelConfirmedModal
        isOpen={cancelConfirmedOpen}
        onOpenChange={() => setCancelConfirmedOpen(false)}
      />
    </>
  )
}
